import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SessionStorageKey } from '@/constants/StorageKeys';
import i18n from '@/plugins/i18n';

interface RespondentLanguageParams {
    /** False while the respondent's answer is loading: nothing is decided before it arrives. */
    isRespondentKnown: boolean;
    surveyLanguage?: string | null;
}

/**
 * Shows the page in the language the respondent reads their survey in: the choice they made in
 * the survey banner first, since the stored preference may briefly lag behind it, then the survey
 * language the server knows for them. Returns that language, once known.
 */
export const useRespondentLanguage = ({
    isRespondentKnown,
    surveyLanguage,
}: RespondentLanguageParams): string | undefined => {
    // Watched, not only read: the module's own language, still being set when the answer arrives,
    // may land after this one and must not have the last word.
    const { i18n: currentI18n } = useTranslation();
    const currentLanguage = currentI18n.language;

    const language = isRespondentKnown
        ? sessionStorage.getItem(SessionStorageKey.SURVEY_LANGUAGE_OVERRIDE) ||
          surveyLanguage ||
          undefined
        : undefined;

    useEffect(() => {
        if (language && language !== currentLanguage) {
            void i18n.changeLanguage(language);
        }
    }, [language, currentLanguage]);

    return language;
};
